import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck, Network, PackageSearch, Timer, BadgeCheck, Puzzle, Users, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { IMG, SERVICES } from "@/lib/siteData";
import QuickQuoteForm from "@/components/QuickQuoteForm";
import { useLang } from "@/context/LangContext";

function Hero() {
    const { t } = useLang();
    const points = [
        { icon: ClipboardCheck, text: t("Des solutions sur mesure pour chaque envoi", "Tailor-made solutions for every shipment") },
        { icon: Network, text: t("Un réseau de partenaires fiables et performants", "A network of reliable, high-performing partners") },
        { icon: PackageSearch, text: t("Un suivi de vos expéditions à chaque étape", "Tracking of your shipments at every stage") },
    ];
    return (
        <section className="relative overflow-hidden bg-[#0b2545]" data-testid="home-hero">
            <img src={IMG.hero} alt={t("Camion, avion et port de conteneurs", "Truck, plane and container port")} className="absolute inset-0 w-full h-full object-cover object-right" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0b2545] via-[#0b2545]/85 to-transparent" />
            <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-end">
                <div className="kz-rise">
                    <div className="kz-eyebrow text-slate-50">Kazem Logistics</div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.2rem] font-bold leading-[1.1] text-slate-50 mt-3" data-testid="home-hero-title">
                        {t("Votre partenaire pour l'organisation de transport de marchandises, ", "Your partner for organising the transport of goods, ")}
                        <span className="text-[#f26b1d]">{t("en France, en Europe et à l'international.", "in France, in Europe and worldwide.")}</span>
                    </h1>
                    <p className="mt-6 text-blue-100/90 max-w-xl leading-relaxed">
                        {t("En tant qu'organisateur de transport, nous coordonnons et optimisons vos flux de marchandises avec un réseau de partenaires fiables et des solutions sur mesure.",
                            "As a transport organiser, we coordinate and optimise your freight flows with a network of reliable partners and tailor-made solutions.")}
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4">
                        <Link to="/devis" data-testid="hero-devis-cta-button" className="kz-btn-primary">{t("Demander un devis", "Request a quote")} <ArrowRight size={16} /></Link>
                        <Link to="/services" data-testid="hero-services-button" className="kz-btn-outline-light">{t("Découvrir nos services", "Discover our services")} <ArrowRight size={15} /></Link>
                    </div>
                </div>
                <ul className="lg:justify-self-end space-y-4 border-l border-blue-100/20 pl-6 kz-rise kz-d2">
                    {points.map((p) => (
                        <li key={p.text} className="flex items-center gap-4 text-sm text-slate-50 max-w-[240px]">
                            <p.icon size={26} className="text-[#f26b1d] shrink-0" strokeWidth={1.5} /> {p.text}
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

function ServiceCard({ s, i }) {
    const { t } = useLang();
    return (
        <Link to={`/services#${s.slug}`} data-testid={`service-card-${s.slug}`} className="group bg-white border border-slate-200 rounded-md overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 flex flex-col kz-rise" style={{ animationDelay: `${i * 70}ms` }}>
            {s.img && <img src={s.img} alt={t(s.title, s.titleEn)} className="h-32 w-full object-cover" />}
            <div className="p-5 flex-1 flex flex-col">
                <s.icon size={30} className="text-[#f26b1d]" strokeWidth={1.5} />
                <h3 className="font-semibold text-[15px] mt-3">{t(s.title, s.titleEn)}</h3>
                <p className="text-[13px] text-slate-600 mt-2 leading-relaxed flex-1">{t(s.desc, s.descEn)}</p>
                <span className="text-[13px] text-[#f26b1d] font-medium mt-4 inline-flex items-center gap-2 group-hover:gap-3 transition-[gap]">{t("En savoir plus", "Learn more")} <ArrowRight size={14} /></span>
            </div>
        </Link>
    );
}

function Services() {
    const { t } = useLang();
    return (
        <section className="py-16 lg:py-20" data-testid="home-services">
            <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[0.9fr_2fr] gap-10">
                <div>
                    <div className="kz-eyebrow text-[#f26b1d]">{t("Nos services", "Our services")}</div>
                    <h2 className="font-display text-3xl font-bold mt-2 leading-tight">{t("Des solutions de transport complètes et sur mesure", "Complete, tailor-made transport solutions")}</h2>
                    <p className="text-sm text-slate-600 mt-4 leading-relaxed">{t("Kazem Logistics organise et coordonne vos expéditions, quelle que soit la nature de votre marchandise et votre destination.", "Kazem Logistics organises and coordinates your shipments, whatever your goods and destination.")}</p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {SERVICES.map((s, i) => <ServiceCard key={s.slug} s={s} i={i} />)}
                    <Link to="/devis" data-testid="service-card-cta" className="group bg-[#0b2545] rounded-md p-6 flex flex-col justify-between hover:-translate-y-1 transition-transform duration-300 kz-rise sm:col-span-2 lg:col-span-1">
                        <div>
                            <div className="kz-eyebrow text-[#f26b1d]">{t("Un besoin spécifique ?", "A specific need?")}</div>
                            <h3 className="font-display text-xl font-bold text-slate-50 mt-3 leading-snug">{t("Nous construisons la solution adaptée à votre flux.", "We build the right solution for your flow.")}</h3>
                        </div>
                        <span className="kz-btn-primary mt-6 w-fit text-[13px]">{t("Demander un devis", "Request a quote")} <ArrowRight size={14} /></span>
                    </Link>
                </div>
            </div>
        </section>
    );
}

function Why() {
    const { t } = useLang();
    const why = [
        { icon: Timer, text: t("Réactivité et disponibilité", "Responsiveness and availability") },
        { icon: BadgeCheck, text: t("Solutions sur mesure selon vos besoins", "Solutions tailored to your needs") },
        { icon: Puzzle, text: t("Des partenaires sélectionnés", "Carefully selected partners") },
        { icon: Users, text: t("Une équipe à votre écoute", "A team that listens") },
    ];
    return (
        <section className="relative bg-[#0b2545] overflow-hidden" data-testid="home-why">
            <div className="grid lg:grid-cols-2">
                <div className="relative z-10 px-5 lg:pl-[max(2rem,calc((100vw-80rem)/2+2rem))] lg:pr-12 py-16">
                    <div className="kz-eyebrow text-[#f26b1d]">{t("Pourquoi choisir Kazem Logistics ?", "Why choose Kazem Logistics?")}</div>
                    <h2 className="font-display text-3xl font-bold text-slate-50 mt-2 leading-tight">{t("Une expertise au service", "Expertise serving")}<br />{t("de votre performance", "your performance")}</h2>
                    <p className="text-sm text-blue-100/85 mt-4 leading-relaxed max-w-lg">
                        {t("Organisateur de transport, nous mettons notre savoir-faire et notre réseau de partenaires au service de vos flux de marchandises. Notre objectif : vous apporter des solutions fiables, réactives et adaptées à vos enjeux.",
                            "As a transport organiser, we put our know-how and partner network at the service of your freight flows. Our goal: to provide reliable, responsive solutions suited to your challenges.")}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-10">
                        {why.map((w) => (
                            <div key={w.text} className="sm:border-r last:border-0 border-blue-100/15 pr-4">
                                <w.icon size={30} className="text-[#f26b1d]" strokeWidth={1.5} />
                                <div className="text-[13px] font-semibold text-slate-50 mt-3 leading-snug">{w.text}</div>
                            </div>
                        ))}
                    </div>
                    <Link to="/a-propos" data-testid="why-about-button" className="kz-btn-outline-light mt-10">{t("En savoir plus sur Kazem Logistics", "Learn more about Kazem Logistics")} <ArrowRight size={15} /></Link>
                </div>
                <div className="relative min-h-[420px]">
                    <img src={IMG.port} alt={t("Porte-conteneurs au port", "Container ship in port")} className="absolute inset-0 w-full h-1/2 lg:h-[55%] object-cover" />
                    <div className="absolute inset-x-0 bottom-0 h-1/2 lg:h-[45%]">
                        <img src={IMG.world} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-l from-[#0b2545]/95 via-[#0b2545]/50 to-transparent" />
                        <div className="absolute right-6 lg:right-[max(2rem,calc((100vw-80rem)/2+2rem))] top-1/2 -translate-y-1/2 max-w-xs text-right sm:text-left">
                            <h3 className="font-display text-xl font-semibold text-slate-50">{t("Une organisation à l'échelle internationale", "Organisation on an international scale")}</h3>
                            <p className="text-[13px] text-blue-100/85 mt-2">{t("Nous organisons vos transports en France, en Europe et à l'international, grâce à notre réseau de partenaires et de solutions multimodales.", "We organise your transport in France, in Europe and internationally, through our network of partners and multimodal solutions.")}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Partners() {
    const { t } = useLang();
    return (
        <section className="py-16 lg:py-20" data-testid="home-partners">
            <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1.1fr_0.9fr_0.9fr] gap-10 items-start">
                <div>
                    <div className="kz-eyebrow text-[#f26b1d]">{t("Ils nous font confiance", "They trust us")}</div>
                    <h2 className="font-display text-3xl font-bold mt-2">{t("Des partenaires qui nous choisissent", "Partners who choose us")}</h2>
                    <p className="text-sm text-slate-600 mt-3">{t("PME et industriels nous confient leurs expéditions pour notre réactivité, notre professionnalisme et notre engagement.", "SMEs and manufacturers entrust us with their shipments for our responsiveness, professionalism and commitment.")}</p>
                    <div className="mt-6 bg-slate-50 rounded-md p-6" data-testid="home-testimonial">
                        <Quote size={22} className="text-[#0b2545] fill-[#0b2545]" />
                        <p className="text-sm text-slate-600 italic mt-3">{t("[Témoignage client à ajouter : citation d'un de vos clients sur votre accompagnement.]", "[Client testimonial to add: a quote from one of your clients.]")}</p>
                        <p className="text-xs text-slate-500 mt-4">{t("— [Nom, fonction, entreprise]", "— [Name, role, company]")}</p>
                    </div>
                    <div className="flex justify-center gap-6 mt-4 text-[#0b2545]"><ChevronLeft size={18} /><ChevronRight size={18} /></div>
                </div>
                <div className="grid grid-cols-3 lg:grid-cols-2 gap-4 self-center" data-testid="home-partner-logos">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="h-16 rounded-md border border-dashed border-slate-300 flex items-center justify-center text-[11px] text-slate-500">{t("Logo partenaire", "Partner logo")}</div>
                    ))}
                </div>
                <div className="relative bg-[#0b2545] rounded-md p-6 overflow-hidden" data-testid="home-quote-box">
                    <img src={IMG.road} alt="" className="absolute right-0 top-0 w-40 h-24 object-cover opacity-30" />
                    <div className="relative">
                        <div className="kz-eyebrow text-[#f26b1d]">{t("Demande de devis", "Quote request")}</div>
                        <h3 className="font-display text-2xl font-bold text-slate-50 mt-1">{t("Parlons de votre projet", "Let's talk about your project")}</h3>
                        <p className="text-[13px] text-blue-100/85 mt-2 mb-5">{t("Vous avez un besoin spécifique ? Remplissez notre formulaire et recevez une réponse rapide de notre équipe.", "Have a specific need? Fill in our form and get a quick reply from our team.")}</p>
                        <QuickQuoteForm testid="home-quote" />
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function HomePage() {
    return (
        <>
            <Hero />
            <Services />
            <Why />
            <Partners />
        </>
    );
}
